/**
 * JP Gems & Jewels - Shared Price Book (persisted, shared across devices).
 * -----------------------------------------------------------------------------
 * A price is keyed by the THREE-field combination the founder uses:
 *
 *      SHAPE | QUALITY | SLIVE   →   { p: purchase price, s: sale price }
 *
 * Seeded from the founder's "VALLENTINE PRICE CHART". Both the Sales Order page
 * (auto-fills the SALE rate) and the Diamond Purchase page (auto-fills the
 * PURCHASE rate) read this one book, so the sales team never re-enters prices.
 * A new combination is learned the first time a price is entered, then reused.
 *
 * Saved to the backend database via sharedStorage under "hariom-erp-pricebook".
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { sharedStorage } from './sharedStorage'

export interface PriceEntry { p: number; s: number }   // purchase, sale

/** Build the canonical, case-insensitive key for a combination. */
export const priceKey = (shape: string, quality: string, slive: string) =>
  [shape, quality, slive].map((x) => (x || '').trim().replace(/\s+/g, ' ').toUpperCase()).join('|')

export const PRICEBOOK_SEED_VERSION = 1

export const SEED_PRICEBOOK: Record<string, PriceEntry> = {
  "ROUND|TOP LB GH SI|+000-000": { p: 26520, s: 28300 },
  "ROUND|TOP LB GH SI|+00-0": { p: 26520, s: 28300 },
  "ROUND|TOP LB GH SI|0-1": { p: 26520, s: 28300 },
  "ROUND|TOP LB GH SI|+1-1.5": { p: 26520, s: 28300 },
  "ROUND|TOP LB GH SI|1.5-2": { p: 26520, s: 28300 },
  "ROUND|TOP LB GH SI|+2-3": { p: 26520, s: 28300 },
  "ROUND|TOP LB GH SI|+3-4": { p: 21420, s: 21500 },
  "ROUND|TOP LB GH SI|+4-5": { p: 21420, s: 21500 },
  "ROUND|TOP LB GH SI|+5-5.5": { p: 21420, s: 21500 },
  "ROUND|TOP LB GH SI|+6-6.5": { p: 21420, s: 21500 },
  "ROUND|TOP LB GH SI|6.5-8": { p: 21420, s: 21500 },
  "ROUND|TOP LB GH SI|8-9.5": { p: 21420, s: 21500 },
  "ROUND|TOP LB GH SI|+9.5-10": { p: 21420, s: 23000 },
  "ROUND|TOP LB GH SI|+10.5-11": { p: 25500, s: 30000 },
  "PRINCES|TOP LC GH SI|2.00 MM": { p: 34680, s: 36000 },
  "MQ|TOP LC GH SI|3.5*1.8": { p: 29580, s: 33000 },
  "MQ|TOP LB GH SI|+7": { p: 32640, s: 35000 },
  "ROUND|TOP LB GH SI|-2.5": { p: 25500, s: 27000 },
  "BUGG|WH GH SI|+3": { p: 28560, s: 42000 },
  "PRINCES|TOP LC GH SI|-7": { p: 39780, s: 50000 },
  "TAPP|WH GH SI|-2": { p: 19380, s: 27500 },
  "BUGG|WH GH SI|-2": { p: 21420, s: 27500 },
  "BUGG|WH GH SI|+4": { p: 33660, s: 48000 },
  "BUGG|WH GH SI|+3.5": { p: 30600, s: 45000 },
  "TAPP|WH GH SI|+2": { p: 21420, s: 29500 },
  "TAPP|WH GH SI|+3": { p: 26520, s: 42000 },
  "B/T|WH GH SI|+2.5": { p: 25500, s: 30000 },
  "EMRALD|SI/GH|1 CT": { p: 99960, s: 114000 },
  "EMRALD|SI/GH|0.2": { p: 35700, s: 45000 },
  "EMRALD|SI/GH|0.70 CT": { p: 66300, s: 77000 },
  "ROUND|SI/GH|+11": { p: 21420, s: 29750 },
  "ROUND|SI/GH|+14": { p: 23970, s: 35000 },
  "ROUND|SI/GH|0.25": { p: 31110, s: 47000 },
  "ROUND|SI/GH|+10.5-11": { p: 16320, s: 21000 },
  "ROUND|SI/GH|+8": { p: 14280, s: 19000 },
  "ROUND|SI/GH|-8": { p: 14280, s: 19000 },
  "EMRALD|BLACK|8.00*6.00": { p: 15300, s: 16000 },
  "EMRALD|BLACK|7.00*5.00": { p: 15300, s: 16000 },
  "MQ|BLACK|4.00*2.00": { p: 14280, s: 17000 },
  "ROUND|BLACK|+00-0": { p: 5304, s: 7000 },
  "ROUND|BLACK|+3.5-4": { p: 4080, s: 6500 },
  "ROUND|CVD|-2.5": { p: 12240, s: 13000 },
  "TRILLION|SI/GH|2.90 MM": { p: 39780, s: 45000 },
  "OVAL|SI/GH|5.00*3.00": { p: 43860, s: 45000 },
  "BUGG|WH GH SI|+2": { p: 23460, s: 29500 },
  "TAPP|WH GH SI|+2.5": { p: 25500, s: 30000 },
  "ROUND|WHITE|-2.5": { p: 28560, s: 32000 },
  "ROUND|WH SI|-8": { p: 19380, s: 25000 },
  "ROUND|WH SI|+11": { p: 34680, s: 42000 },
  "BUGG|WH GH SI|+2.5": { p: 25500, s: 30000 },
  "ROUND|CHAMPAGEN|+3.5-4": { p: 7956, s: 9000 },
  "PEAR|TOP LC GH SI|5.0*3.20": { p: 43860, s: 47500 },
  "TAPP|VEGAS|-2": { p: 0, s: 27500 },
  "BUGG|VEGAS|-2": { p: 0, s: 27500 },
  "BUGG|VEGAS|+2": { p: 0, s: 29500 },
  "TRILLION|WHITE|2.9": { p: 0, s: 45000 },
  "BUGG|VEGAS|+2.5": { p: 0, s: 30000 },
  "HALF MOON|G/SI|5.44*3.40": { p: 96753, s: 128000 },
}

interface PriceBookState {
  book: Record<string, PriceEntry>              // GLOBAL baseline (the founder's imported price chart)
  // Per-PARTY sale prices for Sales Order and per-SUPPLIER purchase prices for
  // Diamond Purchase. Same combination can carry a different price for each party/
  // supplier (goodwill / relationship), and one party's custom price is NEVER used
  // for another party — only the global chart above is the shared fallback.
  partyPrices: Record<string, Record<string, number>>      // party    → combo → sale price
  supplierPrices: Record<string, Record<string, number>>   // supplier → combo → purchase price
  seedVersion: number
  setSale: (key: string, sale: number) => void
  setPurchase: (key: string, purchase: number) => void
  setPartyPrice: (party: string, key: string, sale: number) => void
  setSupplierPrice: (supplier: string, key: string, purchase: number) => void
  setBook: (b: Record<string, PriceEntry>) => void
  setSeedVersion: (v: number) => void
}

export const usePriceBookStore = create<PriceBookState>()(
  persist(
    (set) => ({
      book: {},
      partyPrices: {},
      supplierPrices: {},
      seedVersion: 0,
      setSale: (key, sale) => set((s) => ({ book: { ...s.book, [key]: { p: s.book[key]?.p ?? 0, s: sale } } })),
      setPurchase: (key, purchase) => set((s) => ({ book: { ...s.book, [key]: { p: purchase, s: s.book[key]?.s ?? 0 } } })),
      setPartyPrice: (party, key, sale) => set((s) => ({
        partyPrices: { ...s.partyPrices, [party]: { ...(s.partyPrices[party] || {}), [key]: sale } },
      })),
      setSupplierPrice: (supplier, key, purchase) => set((s) => ({
        supplierPrices: { ...s.supplierPrices, [supplier]: { ...(s.supplierPrices[supplier] || {}), [key]: purchase } },
      })),
      setBook: (b) => set({ book: b }),
      setSeedVersion: (v) => set({ seedVersion: v }),
    }),
    {
      name: 'hariom-erp-pricebook',
      storage: createJSONStorage(() => sharedStorage),
      partialize: (s) => ({ book: s.book, partyPrices: s.partyPrices, supplierPrices: s.supplierPrices, seedVersion: s.seedVersion }),
    }
  )
)
